import React from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  style = {},
  ...props 
}) => {
  const { getComponent, getColor, getSpacing, getFontSize, getBorderRadius, animations } = useThemeTokens();
  
  const buttonComponents = getComponent('buttons');
  
  // Definir variantes de botón
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          ...buttonComponents.primary,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      case 'secondary':
        return {
          ...buttonComponents.secondary,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      case 'ghost':
        return {
          ...buttonComponents.ghost,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      case 'success':
        return {
          backgroundColor: getColor('accent.green'),
          color: getColor('neutral.white'),
          borderRadius: getBorderRadius('md'),
          padding: '12px 24px',
          fontSize: getFontSize('base'),
          fontWeight: '600',
          border: 'none',
          transition: `all ${animations.duration.normal} ease`,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      case 'warning':
        return {
          backgroundColor: getColor('accent.orange'),
          color: getColor('neutral.white'),
          borderRadius: getBorderRadius('md'),
          padding: '12px 24px',
          fontSize: getFontSize('base'),
          fontWeight: '600',
          border: 'none',
          transition: `all ${animations.duration.normal} ease`,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      case 'danger':
        return {
          backgroundColor: getColor('states.error'),
          color: getColor('neutral.white'),
          borderRadius: getBorderRadius('md'),
          padding: '12px 24px',
          fontSize: getFontSize('base'),
          fontWeight: '600',
          border: 'none',
          transition: `all ${animations.duration.normal} ease`,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.6 : 1,
        };
      default:
        return buttonComponents.primary;
    }
  };
  
  // Definir tamaños
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: `${getSpacing('xs')} ${getSpacing('md')}`,
          fontSize: getFontSize('sm'),
        };
      case 'medium':
        return {
          padding: `${getSpacing('sm')} ${getSpacing('lg')}`,
          fontSize: getFontSize('base'),
        };
      case 'large':
        return {
          padding: `${getSpacing('md')} ${getSpacing('xl')}`,
          fontSize: getFontSize('lg'),
        };
      default:
        return {};
    }
  };
  
  // Estilos de hover consistentes
  const baseHover = (bg, color) => ({
    filter: 'brightness(0.95)'
  });

  const buttonStyles = {
    ...getVariantStyles(),
    ...getSizeStyles(),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getSpacing('xs'),
    fontFamily: 'inherit',
    lineHeight: 1,
    textDecoration: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    transition: `all ${animations.duration.normal} ease`,
    boxShadow: 'var(--shadow-sm)'
  };
  
  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };
  
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      style={buttonStyles}
      className={`gaero-button ${className}`}
      {...props}
    >
      {loading && (
        <span 
          className="loading-spinner"
          style={{
            width: '16px',
            height: '16px',
          }}
        />
      )}
      {children}
    </button>
  );
};

export default Button;
